import React, { useState } from "react";
import { ActionButton } from "./ViewValidationsButton.styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ValidationsDialog from "../TenderProcess/ValidationsDialog";
import { cachedValidationResults } from "../../api/api";
import { useToast } from '../ToastProvider/ToastProvider';
import {
    groupByCategory,
    getCategoryStats,
    processValidationData
} from '../TenderProcess/validationUtils';

const STEP_LABELS = {
    source_documents: 'Source Documents Validation',
    summary_spreadsheet: 'Finalized Summary Validation'
};

const ViewValidationsButton = ({ tender }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [validationData, setValidationData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [currentStep, setCurrentStep] = useState(null);
    const { showToast } = useToast();

    const menuOpen = Boolean(menuAnchorEl);
    const availableSteps = tender.available_validation_steps || [];

    // Don't show button if no validation results exist
    if (!availableSteps || availableSteps.length === 0) {
        return null;
    }

    const fetchAndShowValidations = async (step) => {
        setLoading(true);
        setMenuAnchorEl(null); // Close menu if open
        
        try {
            const response = await cachedValidationResults(tender.tender_id, step);
            
            // Transform backend validation data to frontend format
            const transformedValidations = transformValidationData(response.validations || []);
            setValidationData(transformedValidations);
            setCurrentStep(step);
            setDialogOpen(true);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("Error fetching validation results:", error);
            showToast('Failed to load validation results. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleButtonClick = (event) => {
        if (availableSteps.length === 1) {
            // If only one validation result exists, fetch and show it directly
            fetchAndShowValidations(availableSteps[0]);
        } else {
            // If multiple exist, open the menu to let user choose
            setMenuAnchorEl(event.currentTarget);
        }
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleMenuItemClick = (step) => {
        fetchAndShowValidations(step);
    };

    const transformValidationData = (backendValidations) => {
        const transformed = [];
        
        backendValidations.forEach(categoryData => {
            const { category, results } = categoryData;
            
            results.forEach(result => {
                transformed.push({
                    category: category,
                    field_name: result.field_name,
                    tab: result.tab,
                    row: result.line,
                    rule: result.rule,
                    passed: result.valid
                });
            });
        });
        
        return transformed;
    };

    const handleToggle = (category) => {
        setExpanded(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // Process validation data for dialog
    const sortedCategories = validationData ? 
        Object.entries(groupByCategory(processValidationData(validationData)))
            .map(([category, items]) => ({
                category,
                items,
                stats: getCategoryStats(items)
            }))
            .sort((a, b) => b.stats.failed - a.stats.failed)
        : [];

    return (
        <>
            <ActionButton
                data-testid="view-validations-button"
                onClick={handleButtonClick}
                disabled={loading}
                aria-controls={menuOpen ? 'validation-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
            >
                <AssignmentIcon />
            </ActionButton>
            
            {/* Menu for selecting which validation to view (only shown if multiple exist) */}
            <Menu
                id="validation-menu"
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    'aria-labelledby': 'view-validations-button',
                }}
            >
                {availableSteps.map((step) => (
                    <MenuItem 
                        key={step} 
                        onClick={() => handleMenuItemClick(step)}
                        data-testid={`validation-menu-item-${step}`}
                    >
                        <ListItemText primary={STEP_LABELS[step] || step} />
                    </MenuItem>
                ))}
            </Menu>
            
            {/* Dialog for displaying validation results */}
            {validationData && (
                <ValidationsDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    sortedCategories={sortedCategories}
                    expanded={expanded}
                    onToggle={handleToggle}
                    title={currentStep ? STEP_LABELS[currentStep] : 'Validation Results'}
                />
            )}
        </>
    );
};

export default ViewValidationsButton;

